/* eslint-disable no-debugger */
import React from 'react';
import { useEffect } from 'react';
import useKendoReportData from './useKendoReportData';

const useStandardKendoReport = (reportInfoId, reportParameters, detailArrayName) => {
  const [currentReportParameters, setCurrentReportParameters] = React.useState(reportParameters);
  const [formValues, setFormValues] = React.useState(reportParameters);

  // Use the generic report data hook
  const {
    reportData,
    isLoaded
  } = useKendoReportData(reportInfoId, currentReportParameters);

  // Form handling
  const onFormValueChanged = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const onFormDateValueChanged = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const onValueTextPairChanged = (newValue, valuePropertyName, newValueLabel, labelPropertyName) => {
    if (formValues[valuePropertyName] !== newValue
      || formValues[labelPropertyName] !== newValueLabel) {
      setFormValues(setFormValues => ({
        ...setFormValues,
        [valuePropertyName]: newValue,
        [labelPropertyName]: newValueLabel
      }));
      // setIsDirty(true);
    }
  }

  const handleRefresh = async () => {
    setCurrentReportParameters({ ...formValues });
  };

  // Sync formValues when reportParameters prop changes
  useEffect(() => {
    setFormValues(reportParameters);
  }, [reportParameters]);

  // Sub-report (master/detail) columns logic
  const subReportStructureRef = React.useRef(null);
  const generatedSubReportColumnsRef = React.useRef([]);
  const subReportColumnsSetRef = React.useRef(false);

  // Check if reportData has a sub-report (master/detail) column
  const hasSubReportColumn = React.useMemo(() => {
    if (!reportData || reportData.length === 0 || !detailArrayName) {
      return false;
    }

    return Object.keys(reportData[0]).some(key => key.toLowerCase() === detailArrayName.toLowerCase());
  }, [reportData]);

  // Generate columns for sub-report detail grid
  const generateSubReportColumns = React.useMemo(() => {
    // If sub-report columns have been successfully set once, always return the cached value
    if (subReportColumnsSetRef.current && generatedSubReportColumnsRef.current.length > 0) {
      return generatedSubReportColumnsRef.current;
    }

    if (!hasSubReportColumn || !reportData || reportData.length === 0) {
      return [];
    }

    // Find the first row that has sub-report data
    const rowWithSubReportData = reportData.find(row => {
      const subReportKey = Object.keys(row).find(key => key.toLowerCase() === detailArrayName.toLowerCase());

      return subReportKey && row[subReportKey] && Array.isArray(row[subReportKey]) && row[subReportKey].length > 0;
    });

    if (!rowWithSubReportData) {
      return [];
    }

    const subReportKey = Object.keys(rowWithSubReportData).find(key => key.toLowerCase() === detailArrayName.toLowerCase());
    const firstSubReportObj = rowWithSubReportData[subReportKey][0];

    if (!firstSubReportObj || typeof firstSubReportObj !== 'object') {
      return [];
    }

    // Create structure signature for sub-report columns
    const subReportKeys = Object.keys(firstSubReportObj).sort();
    const currentSubReportStructure = subReportKeys.join(',');

    const newSubReportColumns = subReportKeys.map((key) => {
      const title = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

      const sampleValue = firstSubReportObj[key];
      let width = '120px';

      if (typeof sampleValue === 'string' && sampleValue.length >= 20) {
        width = '350px';
      } else if (title.length > 20) {
        width = '200px';
      } else if (typeof sampleValue === 'string' && sampleValue.length > 12) {
        width = '200px';
      } else if (typeof sampleValue === 'number') {
        width = '180px';
      } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('percent')) {
        width = '180px';
      } else if (key.toLowerCase().includes('id')) {
        width = '80px';
      }

      const columnConfig = {
        id: key,
        field: key,
        title: title,
        width: width
      };

      // Add date formatting if the field appears to be a date
      if (key.toLowerCase().includes('date') && typeof sampleValue === 'string') {
        columnConfig.format = '{0:MM/dd/yyyy}';
      } else if (typeof sampleValue === 'boolean') {
        columnConfig.cell = (props) => 
          React.createElement('td', null, 
            props.dataItem[props.field] ? 'Yes' : 'No'
          );
      } else if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('progression')) {
        columnConfig.format = '{0:p1}';
      }

      return columnConfig;
    });

    subReportColumnsSetRef.current = true;
    subReportStructureRef.current = currentSubReportStructure;
    generatedSubReportColumnsRef.current = newSubReportColumns;

    return newSubReportColumns;
  }, [reportData, hasSubReportColumn]);

  // Helper function to check if a row has sub-report data
  const hasRowSubReportData = (dataItem) => {
    const subReportKey = Object.keys(dataItem).find(key => key.toLowerCase() === detailArrayName.toLowerCase());
    return !!(subReportKey && dataItem[subReportKey] && Array.isArray(dataItem[subReportKey]) && dataItem[subReportKey].length > 0);
  };

  return {
    reportData,
    isLoaded,
    formValues,
    onFormValueChanged,
    onFormDateValueChanged,
    handleRefresh,
    hasSubReportColumn,
    generateSubReportColumns,
    hasRowSubReportData,
    onValueTextPairChanged
  };
};

export default useStandardKendoReport;
